/**
 * Product Image Hover Slider — TerraDesi Theme
 * Smooth overlay slider: arrows, pill-dots, progress bar, swipe, auto-slide
 */

(function () {
  'use strict';

  var CONFIG = {
    slideInterval : 1100,  // ms between auto-advances
    slideDuration : 350,   // ms for the slide transform
    autoReturn    : true   // snap back to slide 0 on mouse-leave
  };

  /* Card image display width — used for Shopify CDN resizing */
  var IMAGE_WIDTH = 600;

  var initialized = new WeakSet();

  /* ── helpers ─────────────────────────────────────────────── */

  function toHttps(url) {
    if (!url) return url;
    return url.startsWith('//') ? 'https:' + url : url;
  }

  /**
   * Append ?width=N to a Shopify CDN URL so we don't download
   * full-resolution images for small card thumbnails.
   */
  function resizeUrl(url) {
    if (!url) return url;
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    return url + sep + 'width=' + IMAGE_WIDTH;
  }

  function getHandle(wrapper) {
    var link = wrapper.querySelector('a[href*="/products/"]');
    if (!link) return null;
    var m = link.href.match(/\/products\/([^?#/]+)/);
    return m ? m[1] : null;
  }

  function findMediaContainer(wrapper) {
    return (
      wrapper.querySelector('.media--hover-effect') ||
      wrapper.querySelector('.card__media .media') ||
      wrapper.querySelector('.card__media') ||
      wrapper.querySelector('.media') ||
      null
    );
  }

  /* ── SVG chevrons ─────────────────────────────────────────── */

  function chevronSVG(dir) {
    var pts = dir === 'prev' ? '15 18 9 12 15 6' : '9 18 15 12 9 6';
    return '<svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="' + pts + '"></polyline></svg>';
  }

  /* ── build overlay UI ─────────────────────────────────────── */

  function buildOverlay(mediaEl, urls) {
    /* ensure positioning context (media is already absolute, but guard anyway) */
    if (window.getComputedStyle(mediaEl).position === 'static') {
      mediaEl.style.position = 'relative';
    }

    /* ── sliding strip ── */
    var strip = document.createElement('div');
    strip.className = 'phs-strip';

    urls.forEach(function (url, i) {
      var slide = document.createElement('div');
      slide.className = 'phs-slide';

      var img = document.createElement('img');
      img.src       = resizeUrl(url);
      img.srcset    = '';
      img.alt       = '';
      img.loading   = i < 2 ? 'eager' : 'lazy';
      img.className = 'phs-slide-img';

      slide.appendChild(img);
      strip.appendChild(slide);
    });

    mediaEl.appendChild(strip);

    /* ── bottom gradient (improves contrast for dots/arrows) ── */
    var gradient = document.createElement('div');
    gradient.className = 'phs-gradient';
    mediaEl.appendChild(gradient);

    /* ── dots ── */
    var dotsWrap = document.createElement('div');
    dotsWrap.className = 'phs-dots';

    var dots = urls.map(function (_, i) {
      var dot = document.createElement('span');
      dot.className = 'phs-dot' + (i === 0 ? ' phs-dot--active' : '');
      dot.setAttribute('aria-label', 'Image ' + (i + 1));
      dotsWrap.appendChild(dot);
      return dot;
    });

    mediaEl.appendChild(dotsWrap);

    /* ── arrows ── */
    var prevBtn = document.createElement('button');
    prevBtn.type      = 'button';
    prevBtn.className = 'phs-arrow phs-arrow--prev';
    prevBtn.setAttribute('aria-label', 'Previous image');
    prevBtn.innerHTML = chevronSVG('prev');

    var nextBtn = document.createElement('button');
    nextBtn.type      = 'button';
    nextBtn.className = 'phs-arrow phs-arrow--next';
    nextBtn.setAttribute('aria-label', 'Next image');
    nextBtn.innerHTML = chevronSVG('next');

    mediaEl.appendChild(prevBtn);
    mediaEl.appendChild(nextBtn);

    /* ── progress bar ── */
    var progress = document.createElement('div');
    progress.className = 'phs-progress';
    var progressFill = document.createElement('div');
    progressFill.className = 'phs-progress-fill';
    progress.appendChild(progressFill);
    mediaEl.appendChild(progress);

    return { strip: strip, dots: dots, prevBtn: prevBtn, nextBtn: nextBtn, progressFill: progressFill };
  }

  /* ── per-card init ────────────────────────────────────────── */

  function initCard(wrapper) {
    if (initialized.has(wrapper)) return;
    var handle = getHandle(wrapper);
    if (!handle) return;

    initialized.add(wrapper);

    fetch('/products/' + handle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (product) {
        var urls = (product.images || []).map(toHttps).filter(Boolean);
        if (urls.length < 2) return;

        var mediaEl = findMediaContainer(wrapper);
        if (!mediaEl) return;

        /* eager-preload first 2 images */
        urls.slice(0, 2).forEach(function (u) {
          var i = new Image();
          i.src = resizeUrl(u);
        });

        var ui = buildOverlay(mediaEl, urls);
        var strip       = ui.strip;
        var dots        = ui.dots;
        var prevBtn     = ui.prevBtn;
        var nextBtn     = ui.nextBtn;
        var progressFill = ui.progressFill;

        var index  = 0;
        var timer  = null;
        var active = false;
        var busy   = false;
        var paused = false;

        /* ── go to slide ──────────────────────────────────── */
        function goTo(next, instant) {
          if (next === index && !instant) return;
          busy  = true;
          index = next;

          var dur = instant ? 0 : CONFIG.slideDuration;

          /* update strip transition */
          if (dur) {
            strip.style.transition =
              'opacity 250ms ease, transform ' + dur + 'ms cubic-bezier(.4,0,.2,1)';
          } else {
            strip.style.transition = 'opacity 250ms ease';
          }
          strip.style.transform = 'translateX(-' + (index * 100) + '%)';

          /* update dots */
          dots.forEach(function (d, i) {
            d.classList.toggle('phs-dot--active', i === index);
          });

          /* update progress bar */
          var pct = urls.length > 1
            ? Math.round((index / (urls.length - 1)) * 100)
            : 100;
          progressFill.style.transition = instant ? 'none' : 'width 280ms ease';
          progressFill.style.width = pct + '%';

          setTimeout(function () { busy = false; }, dur || 0);
        }

        /* ── dot clicks ───────────────────────────────────── */
        dots.forEach(function (dot, i) {
          dot.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            paused = true;
            clearInterval(timer);
            goTo(i);
          });
        });

        /* ── arrow clicks ─────────────────────────────────── */
        prevBtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          paused = true;
          clearInterval(timer);
          goTo((index - 1 + urls.length) % urls.length);
        });

        nextBtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          paused = true;
          clearInterval(timer);
          goTo((index + 1) % urls.length);
        });

        /* ── touch / swipe ────────────────────────────────── */
        var touchStartX = 0;

        mediaEl.addEventListener('touchstart', function (e) {
          touchStartX = e.changedTouches[0].clientX;
        }, { passive: true });

        mediaEl.addEventListener('touchend', function (e) {
          var diff = touchStartX - e.changedTouches[0].clientX;
          if (Math.abs(diff) > 35) {
            paused = true;
            clearInterval(timer);
            goTo(diff > 0
              ? (index + 1) % urls.length
              : (index - 1 + urls.length) % urls.length
            );
          }
        }, { passive: true });

        /* ── auto-play ────────────────────────────────────── */
        function startAutoPlay() {
          clearInterval(timer);
          if (paused) return;
          timer = setInterval(function () {
            if (!busy && !paused) goTo((index + 1) % urls.length);
          }, CONFIG.slideInterval);
        }

        /* ── hover handlers ───────────────────────────────── */
        function onEnter() {
          if (active) return;
          active = true;
          paused = false;
          startAutoPlay();
        }

        function onLeave() {
          active = false;
          paused = false;
          clearInterval(timer);
          timer = null;
          if (CONFIG.autoReturn && index !== 0) {
            goTo(0);
          }
        }

        wrapper.addEventListener('mouseenter', onEnter);
        wrapper.addEventListener('mouseleave', onLeave);
      })
      .catch(function () {
        initialized.delete(wrapper);
      });
  }

  /* ── batch init + MutationObserver ───────────────────────── */

  function initAll() {
    document.querySelectorAll('.card-wrapper.product-card-wrapper').forEach(initCard);
  }

  var debounceTimer = null;
  new MutationObserver(function () {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(initAll, 400);
  }).observe(document.body, { childList: true, subtree: true });

  function boot() {
    initAll();
    document.addEventListener('shopify:section:load', initAll);
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', boot)
    : boot();

}());
